import { useEffect, useRef, useState } from 'react';
import { cn } from '../lib/utils';
import { Input } from './ui/input';

const API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

function loadScript() {
  const existingScript = document.getElementById('google-maps-places-script');
  if (existingScript) {
    if (window.google?.maps?.places) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      const handleLoad = () => {
        existingScript.removeEventListener('load', handleLoad);
        existingScript.removeEventListener('error', handleError);
        resolve();
      };
      const handleError = () => {
        existingScript.removeEventListener('load', handleLoad);
        existingScript.removeEventListener('error', handleError);
        reject(new Error('Failed to load Google Maps script'));
      };
      existingScript.addEventListener('load', handleLoad);
      existingScript.addEventListener('error', handleError);
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = 'google-maps-places-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places`;
    script.async = true;
    script.onload = resolve;
    script.onerror = reject;
    if (!window.gm_authFailure) {
      window.gm_authFailure = () => {};
    }
    document.head.appendChild(script);
  });
}

const hasHouseOrFlatNumber = (value) => {
  const firstSegment = value.split(',')[0]?.trim() || '';
  return (
    /^\d+[a-z]?\b/i.test(firstSegment) ||
    /\b(flat|apartment|apt|unit|suite|room)\s*\d+[a-z]?\b/i.test(firstSegment)
  );
};

export const AddressInput = ({
  onAddressSelect,
  defaultValue = '',
  placeholder = 'Start typing an address or postcode...',
  className,
  label,
  required = false,
  helperText = 'Example: Flat 4, 12 High Street, London SW1A 1AA, UK',
  onWarning,
}) => {
  const inputRef = useRef(null);
  const selectedFromSuggestionRef = useRef(false);
  const blurTimeoutRef = useRef(null);
  const onAddressSelectRef = useRef(onAddressSelect);
  const onWarningRef = useRef(onWarning);
  const warningDismissedRef = useRef(false);
  const pendingSelectionValidationRef = useRef(false);
  const placesLibraryRef = useRef(null);
  const requestSeqRef = useRef(0);

  const [inputValue, setInputValue] = useState(defaultValue);
  const [showWarning, setShowWarning] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  useEffect(() => {
    onAddressSelectRef.current = onAddressSelect;
  });

  useEffect(() => {
    onWarningRef.current = onWarning;
  });

  useEffect(() => {
    warningDismissedRef.current = warningDismissed;
  }, [warningDismissed]);

  useEffect(() => {
    onWarningRef.current?.(showWarning);
  }, [showWarning]);

  useEffect(() => {
    if (!pendingSelectionValidationRef.current) return;
    pendingSelectionValidationRef.current = false;
    updateWarningFromValue(inputValue || '');
  }, [inputValue]);

  useEffect(() => {
    setInputValue(defaultValue || '');
    setShowWarning(false);
    setWarningDismissed(false);
    setSuggestions([]);
    setSuggestionsOpen(false);
    setActiveSuggestionIndex(-1);
  }, [defaultValue]);

  useEffect(() => {
    if (!API_KEY) return;

    let isMounted = true;

    const init = async () => {
      try {
        await loadScript();
        if (!isMounted) return;
        placesLibraryRef.current = await window.google.maps.importLibrary('places');
      } catch (error) {
        // Fail silently and keep manual entry available.
      }
    };

    init();

    return () => {
      isMounted = false;
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
      placesLibraryRef.current = null;
    };
  }, []);

  const updateWarningFromValue = (value) => {
    const trimmed = value.trim();
    if (trimmed && !hasHouseOrFlatNumber(trimmed) && !warningDismissedRef.current) {
      setShowWarning(true);
    } else {
      setShowWarning(false);
    }
  };

  const validateFromCurrentInput = (fallbackValue = '') => {
    const current = inputRef.current?.value ?? fallbackValue;
    updateWarningFromValue(current);
  };

  const renderHighlightedMainText = (text, matchedSubstrings) => {
    if (!text) return null;
    if (!Array.isArray(matchedSubstrings) || matchedSubstrings.length === 0) {
      return text;
    }

    const orderedMatches = [...matchedSubstrings]
      .filter((match) => Number.isInteger(match?.offset) && Number.isInteger(match?.length) && match.length > 0)
      .sort((a, b) => a.offset - b.offset);

    if (orderedMatches.length === 0) {
      return text;
    }

    const nodes = [];
    let cursor = 0;

    orderedMatches.forEach((match, index) => {
      const start = Math.max(0, Math.min(text.length, match.offset));
      const end = Math.max(start, Math.min(text.length, start + match.length));

      if (start > cursor) {
        nodes.push(
          <span key={`plain-${index}-${cursor}`}>
            {text.slice(cursor, start)}
          </span>
        );
      }

      if (end > start) {
        nodes.push(
          <span key={`match-${index}-${start}`} className="font-semibold text-slate-900">
            {text.slice(start, end)}
          </span>
        );
      }

      cursor = Math.max(cursor, end);
    });

    if (cursor < text.length) {
      nodes.push(
        <span key={`tail-${cursor}`}>
          {text.slice(cursor)}
        </span>
      );
    }

    return nodes;
  };

  const fetchSuggestions = async (value) => {
    const lib = placesLibraryRef.current;
    const trimmed = value.trim();

    if (!lib || trimmed.length < 3) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      setActiveSuggestionIndex(-1);
      return;
    }

    const requestId = ++requestSeqRef.current;

    try {
      const { suggestions: results } =
        await lib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: trimmed,
          includedRegionCodes: ['gb'],
          includedPrimaryTypes: ['geocode'],
        });

      if (requestId !== requestSeqRef.current) return;

      const next = (results || []).map((item) => {
        const pred = item.placePrediction;
        return {
          placeId: pred.placeId,
          description: pred.text?.toString() || '',
          mainText: pred.mainText?.toString() || pred.text?.toString() || '',
          secondaryText: pred.secondaryText?.toString() || '',
          matchedSubstrings: (pred.mainText?.matches || []).map((m) => ({
            offset: m.startOffset,
            length: m.endOffset - m.startOffset,
          })),
        };
      });

      setSuggestions(next);
      setSuggestionsOpen(next.length > 0);
      setActiveSuggestionIndex(next.length > 0 ? 0 : -1);
    } catch {
      if (requestId === requestSeqRef.current) {
        setSuggestions([]);
        setSuggestionsOpen(false);
        setActiveSuggestionIndex(-1);
      }
    }
  };

  const selectSuggestion = async (suggestion) => {
    if (!suggestion) return;

    selectedFromSuggestionRef.current = true;
    setSuggestions([]);
    setSuggestionsOpen(false);
    setActiveSuggestionIndex(-1);

    const lib = placesLibraryRef.current;
    if (!lib) {
      pendingSelectionValidationRef.current = true;
      setInputValue(suggestion.description);
      onAddressSelectRef.current?.({
        formatted: suggestion.description,
        place_id: suggestion.placeId,
      });
      return;
    }

    try {
      const place = new lib.Place({ id: suggestion.placeId });
      await place.fetchFields({
        fields: ['addressComponents', 'formattedAddress', 'id'],
      });

      const components = place.addressComponents || [];
      const get = (type) =>
        components.find((c) => c.types.includes(type))?.longText || '';

      const line1 = [get('street_number'), get('route')]
        .filter(Boolean)
        .join(' ');
      const town = get('postal_town') || get('locality');
      const county = get('administrative_area_level_2');
      const postcode = get('postal_code');
      const formatted =
        place.formattedAddress ||
        [line1, town, county, postcode].filter(Boolean).join(', ');

      pendingSelectionValidationRef.current = true;
      setInputValue(formatted);
      onAddressSelectRef.current?.({
        line1,
        line2: '',
        town,
        county,
        postcode,
        formatted,
        place_id: place.id || suggestion.placeId,
      });
    } catch {
      pendingSelectionValidationRef.current = true;
      setInputValue(suggestion.description);
      onAddressSelectRef.current?.({
        formatted: suggestion.description,
        place_id: suggestion.placeId,
      });
    }
  };

  const handleChange = (event) => {
    const nextValue = event.target.value;
    setInputValue(nextValue);
    fetchSuggestions(nextValue);

    if (showWarning && hasHouseOrFlatNumber(nextValue)) {
      setShowWarning(false);
    }
  };

  const handleKeyDown = (event) => {
    if (!suggestionsOpen || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSuggestionIndex((prev) => (prev + 1) % suggestions.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSuggestionIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
      return;
    }

    if (event.key === 'Enter' && activeSuggestionIndex >= 0) {
      event.preventDefault();
      selectSuggestion(suggestions[activeSuggestionIndex]);
      return;
    }

    if (event.key === 'Escape') {
      setSuggestionsOpen(false);
      setActiveSuggestionIndex(-1);
    }
  };

  const handleBlur = () => {
    blurTimeoutRef.current = setTimeout(() => {
      setSuggestionsOpen(false);

      const trimmed = inputRef.current?.value.trim() || inputValue.trim();
      const fromSuggestion = selectedFromSuggestionRef.current;

      if (fromSuggestion) {
        selectedFromSuggestionRef.current = false;
      }

      validateFromCurrentInput(trimmed);

      if (!fromSuggestion) {
        onAddressSelectRef.current?.({ formatted: trimmed });
      }
    }, 120);
  };

  const handleDismiss = () => {
    setShowWarning(false);
    setWarningDismissed(true);
  };

  return (
    <div className={cn('w-full relative', className)}>
      {label && (
        <label className="block text-sm font-medium mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <Input
        ref={inputRef}
        value={inputValue}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={() => {
          if (suggestions.length > 0) {
            setSuggestionsOpen(true);
          }
        }}
      />

      {helperText ? (
        <p className="mt-1.5 text-xs text-muted-foreground">
          {helperText}
        </p>
      ) : null}

      {suggestionsOpen && suggestions.length > 0 && (
        <ul className="absolute z-[100000] mt-1 max-h-64 w-full overflow-auto rounded-md border bg-white shadow-lg">
          {suggestions.map((suggestion, index) => (
            <li
              key={suggestion.placeId}
              className={cn(
                'cursor-pointer px-3 py-2 text-sm',
                index === activeSuggestionIndex ? 'bg-slate-100' : 'hover:bg-slate-50'
              )}
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() => selectSuggestion(suggestion)}
            >
              <div className="leading-tight">
                <div className="text-slate-900">
                  {renderHighlightedMainText(suggestion.mainText, suggestion.matchedSubstrings)}
                </div>
                {suggestion.secondaryText && (
                  <div className="text-xs text-slate-500 mt-0.5">{suggestion.secondaryText}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {showWarning && (
        <p className="mt-1.5 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          Please include a house or flat number. If your property has a name only (e.g. Rose Cottage),{' '}
          <button
            type="button"
            className="underline font-medium"
            onClick={handleDismiss}
          >
            dismiss this
          </button>.
        </p>
      )}
    </div>
  );
};
